import { useState } from 'react';
import { Button } from '@simplywallst/ui-core/next';
import CheckSolid from '@simplywallst/ui-core/icons/CheckSolid';

import { useNarrativeSelectValuationOption } from '@/hooks/narrative/useNarrativeSelectValuationOption';
import { useTracking } from 'react-tracking';
import GuestRegistrationModal from '@/components/GuestRegistrationModal';
import useUser from '@/hooks/useUser';
import { useNarrativeInvalidationBoundary } from '../NarrativeInvalidationBoundary/NarrativeInvalidationBoundary';

type Props = {
  narrativeId: string;
  companyId: string;
  isPrimary: boolean;
  onError: () => void;
  valuationType: 'USER_DEFINED' | 'DCF' | 'ANALYSTS';
};

export const NarrativeSetFairValue = ({
  narrativeId,
  companyId,
  isPrimary,
  onError,
  valuationType,
}: Props) => {
  const [isSettingFairValue, setIsSettingFairValue] = useState(false);
  const { mutateAsync: setFairValue } = useNarrativeSelectValuationOption();
  const { invalidateSetPrimary } = useNarrativeInvalidationBoundary();
  const { trackEvent } = useTracking();
  const { data: user } = useUser();
  const [isGuestRegistrationModalOpen, setGuestRegistrationModal] =
    useState(false);

  const handleOnClick = async () => {
    if (!user.loggedIn) return setGuestRegistrationModal(true);
    if (isPrimary) return;

    setIsSettingFairValue(true);

    try {
      await setFairValue({
        input: {
          companyId,
          narrativeId,
          type: valuationType,
        },
      });

      trackEvent({
        action: 'save',
        companyId,
        valuationType,
        narrativeId,
      });

      await invalidateSetPrimary({ narrativeId, companyId });
    } catch (err) {
      console.error('Unable to set narrative as primary', err);
      onError();
    }
    setIsSettingFairValue(false);
  };

  return (
    <>
      <Button
        isLoading={isSettingFairValue}
        isDisabled={isSettingFairValue}
        variant={isPrimary ? 'secondary' : 'outline'}
        size="large"
        onClick={handleOnClick}
      >
        {isPrimary ? (
          <>
            <CheckSolid />
            Fair Value
          </>
        ) : isSettingFairValue ? (
          'Saving'
        ) : (
          'Set as Fair Value'
        )}
      </Button>
      {isGuestRegistrationModalOpen && (
        <GuestRegistrationModal
          onClose={() => setGuestRegistrationModal(false)}
          onLoginSuccess={() => setGuestRegistrationModal(false)}
          onRegisterSuccess={() => setGuestRegistrationModal(false)}
          canClose
          removeWindowScroll
        />
      )}
    </>
  );
};
